import { useEffect, useRef, useState } from "react";
import type { PointerEvent as ReactPointerEvent } from "react";
import type { TugTeam } from "@/lib/types";
import { TEAM_BG, TEAM_RING, TEAM_TEXT } from "../utils";

interface Props {
  team: TugTeam;
  name: string;
  tapSec: number;
  submitting: boolean;
  /** 두드리기가 끝나면 최종 터치수를 넘긴다 (사용자가 버튼을 누를 필요 없음) */
  onFinish: (taps: number) => void;
}

type Phase = "count" | "go" | "tap";

// 참가 플로우 전체화면.
//   3 → 2 → 1 → 시작! → tap_sec 초 동안 두드리기 → 자동 제출
// 카운트는 useRef 로 세고, 화면 표시는 requestAnimationFrame 으로 갱신한다.
// (매 탭마다 setState 하면 렉이 걸려 실제로 손해를 본다)
export default function TugPlay({ team, name, tapSec, submitting, onFinish }: Props) {
  const [phase, setPhase] = useState<Phase>("count");
  const [countNum, setCountNum] = useState(3);
  const [taps, setTaps] = useState(0);
  const [remain, setRemain] = useState(tapSec);

  const countRef = useRef(0);
  const startRef = useRef(0);
  const liveRef = useRef(false);
  const submittedRef = useRef(false);
  const btnRef = useRef<HTMLButtonElement | null>(null);

  // onFinish 는 매 렌더마다 새로 올 수 있으므로 ref 로 고정한다(루프 리셋 방지)
  const finishRef = useRef(onFinish);
  useEffect(() => {
    finishRef.current = onFinish;
  }, [onFinish]);

  // 3 → 2 → 1
  useEffect(() => {
    if (phase !== "count") return;
    let n = 3;
    setCountNum(3);
    const t = window.setInterval(() => {
      n -= 1;
      if (n <= 0) {
        window.clearInterval(t);
        setPhase("go");
      } else {
        setCountNum(n);
      }
    }, 1000);
    return () => window.clearInterval(t);
  }, [phase]);

  // 시작!
  useEffect(() => {
    if (phase !== "go") return;
    const t = window.setTimeout(() => setPhase("tap"), 900);
    return () => window.clearTimeout(t);
  }, [phase]);

  // 두드리기 루프 — 시간은 시작 시각(Date.now) 기준으로 계산한다.
  useEffect(() => {
    if (phase !== "tap") return;
    countRef.current = 0;
    setTaps(0);
    setRemain(tapSec);
    startRef.current = Date.now();
    liveRef.current = true;

    let raf = 0;
    let last = 0;
    const tick = () => {
      const nowMs = Date.now();
      const left = Math.max(0, tapSec - (nowMs - startRef.current) / 1000);
      if (nowMs - last > 80) {
        last = nowMs;
        setRemain(left);
        setTaps(countRef.current);
      }
      if (left <= 0) {
        liveRef.current = false;
        setTaps(countRef.current);
        setRemain(0);
        if (!submittedRef.current) {
          submittedRef.current = true;
          finishRef.current(countRef.current);
        }
        return;
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [phase, tapSec]);

  const onDown = (e: ReactPointerEvent<HTMLButtonElement>) => {
    e.preventDefault();
    if (!liveRef.current) return;
    countRef.current += 1;
    if (typeof navigator !== "undefined" && typeof navigator.vibrate === "function") {
      navigator.vibrate(10);
    }
    const el = btnRef.current;
    if (el) {
      el.classList.remove("is-tap");
      // 강제 리플로우로 애니메이션을 다시 트리거한다.
      void el.offsetWidth;
      el.classList.add("is-tap");
    }
  };

  const pct = tapSec > 0 ? (remain / tapSec) * 100 : 0;
  const ring = TEAM_RING[team];

  return (
    <div
      className="fixed inset-0 z-[260] flex flex-col items-center justify-center bg-background-50 px-6"
      role="dialog"
      aria-modal="true"
    >
      {phase === "count" && (
        <div className="text-center">
          <p className={`font-heading text-base font-bold ${TEAM_TEXT[team]}`}>{name}</p>
          <p
            key={countNum}
            className="tug-count-pop mt-6 font-heading text-[120px] font-extrabold leading-none text-foreground-950"
          >
            {countNum}
          </p>
          <p className="mt-6 text-sm text-foreground-500">곧 시작합니다</p>
        </div>
      )}

      {phase === "go" && (
        <p className="tug-count-pop font-heading text-7xl font-extrabold text-primary-600">
          시작!
        </p>
      )}

      {phase === "tap" && (
        <div className="flex w-full max-w-md flex-col items-center">
          <div className="mb-8 flex items-center gap-4">
            <div
              className="rounded-full p-1"
              style={{
                background: `conic-gradient(${ring} ${pct}%, oklch(var(--background-200)) ${pct}% 100%)`,
              }}
            >
              <div className="flex h-20 w-20 items-center justify-center rounded-full bg-background-50">
                <span className="font-heading text-2xl font-extrabold tabular-nums text-foreground-950">
                  {Math.ceil(remain)}
                </span>
              </div>
            </div>
            <div>
              <p className={`font-heading text-lg font-bold ${TEAM_TEXT[team]}`}>{name}</p>
              <p className="text-sm text-foreground-500">화면을 두드리세요!</p>
            </div>
          </div>

          <button
            ref={btnRef}
            type="button"
            onPointerDown={onDown}
            onContextMenu={(e) => e.preventDefault()}
            aria-label="두드리기"
            className={`tug-tap-btn flex h-[250px] w-[250px] select-none items-center justify-center rounded-full ${TEAM_BG[team]}`}
          >
            <span className="font-heading text-8xl font-extrabold tabular-nums">
              {taps}
            </span>
          </button>

          <p className="mt-8 text-center text-sm text-foreground-500">
            시간이 끝나면 기록이 자동으로 전송됩니다
          </p>
          {submitting && (
            <p className="mt-2 text-sm font-semibold text-primary-600">
              기록을 보내는 중…
            </p>
          )}
        </div>
      )}
    </div>
  );
}